import { useEffect, useState } from 'react'
import { Button, Modal, TextArea, TextInput, Toggle } from '../../../shared/ui'
import { useLieferanten } from '../../verwaltung/lieferanten/api/lieferantenApi'
import type { SchoolSupplierIdentifier } from './types'
import { useDeleteIdentifier, useUpsertIdentifier } from './identifiersApi'

interface Props {
  open: boolean
  schoolId: string
  schoolName: string
  /** When provided, dialog is in EDIT mode — fields prefilled from this entry. */
  initial: SchoolSupplierIdentifier | null
  /** When creating: which Lieferant the new entry is for. Ignored in edit mode. */
  lieferantId: string | null
  /**
   * If true on create, indicates a current entry exists for the same
   * (school, lieferant). Default-on the "Vorgänger schließen" toggle.
   */
  hasExistingCurrent: boolean
  /** Die bereits offenen Kennungen dieses Lieferanten an dieser Schule. */
  currentCodes?: string[]
  onClose: () => void
}

const today = () => new Date().toISOString().slice(0, 10)

export default function IdentifierEditDialog({
  open,
  schoolId,
  schoolName,
  initial,
  lieferantId,
  hasExistingCurrent,
  currentCodes = [],
  onClose,
}: Props) {
  const isEdit = !!initial
  const effectiveLieferantId = initial?.lieferantId ?? lieferantId ?? ''

  const { data: lieferanten = [] } = useLieferanten()
  const lieferant = lieferanten.find((l) => l.id === effectiveLieferantId)
  const istRosenkrantz = effectiveLieferantId === 'rosenkrantz'

  const upsert = useUpsertIdentifier()
  const remove = useDeleteIdentifier()

  const [customerNumber, setCustomerNumber] = useState('')
  const [validFrom, setValidFrom] = useState(today())
  const [validTo, setValidTo] = useState<string>('')
  const [notes, setNotes] = useState('')
  const [closeCurrent, setCloseCurrent] = useState(true)
  const [toggleBeruehrt, setToggleBeruehrt] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleteStage, setDeleteStage] = useState(0)

  // Eine Kennung wie '51012-147' traegt das Lieferanten-KONTO vor dem Strich und
  // die Lieferadresse dahinter. Zwei Konten koennen dieselbe Schule beliefern —
  // dann sind die Kennungen NEBENEINANDER gueltig und keine loest die andere ab.
  // Ohne diese Unterscheidung schliesst der Standardvorschlag die erste Kennung,
  // und jeder kuenftige Beleg des ersten Kontos landet ohne Zuordnung.
  const kontoVon = (code: string) => {
    const i = code.indexOf('-')
    return i > 0 ? code.slice(0, i) : null
  }
  const neuesKonto = kontoVon(customerNumber.trim())
  const istZweitesKonto =
    !!neuesKonto &&
    currentCodes.length > 0 &&
    currentCodes.every((c) => {
      const k = kontoVon(c)
      return k != null && k !== neuesKonto
    })

  // Vorschlag nachziehen, solange der Nutzer den Schalter nicht selbst angefasst hat.
  useEffect(() => {
    if (isEdit || !hasExistingCurrent || toggleBeruehrt) return
    setCloseCurrent(!istZweitesKonto)
  }, [istZweitesKonto, isEdit, hasExistingCurrent, toggleBeruehrt])

  useEffect(() => {
    if (!open) return
    if (initial) {
      setCustomerNumber(initial.customerNumber)
      setValidFrom(initial.validFrom)
      setValidTo(initial.validTo ?? '')
      setNotes(initial.notes ?? '')
      setCloseCurrent(false)
    } else {
      setCustomerNumber('')
      setValidFrom(today())
      setValidTo('')
      setNotes('')
      setCloseCurrent(hasExistingCurrent)
      setToggleBeruehrt(false)
    }
    setError(null)
    setDeleteStage(0)
  }, [open, initial, hasExistingCurrent])

  // Eine Rosenkrantz-Kennung ohne Adress-id ("51012" statt "51012-148") wuerde
  // alle Lieferadressen des Kontos auf diese eine Schule buchen (Rechnung
  // 3195254, Kielortallee → Carl-Cohn). Der Server lehnt das ohnehin mit 400 ab
  // (bzw. mig 317 per CHECK-Constraint) — hier schon vor dem Absenden sperren.
  const istBlankeRosenkrantzKennung = istRosenkrantz && !customerNumber.trim().includes('-')
  const canSave = !!customerNumber.trim() && !!validFrom && !!effectiveLieferantId
    && !(istBlankeRosenkrantzKennung && !validTo)

  async function handleDelete() {
    if (!initial) return
    if (deleteStage < 1) { setDeleteStage(1); return }
    try {
      setError(null)
      await remove.mutateAsync(initial.id)
      onClose()
    } catch (e) {
      setError(String(e))
    }
  }

  async function handleSave() {
    if (!canSave) return
    try {
      setError(null)
      await upsert.mutateAsync({
        id: initial?.id,
        schoolId,
        lieferantId: effectiveLieferantId,
        customerNumber: customerNumber.trim(),
        validFrom,
        validTo: validTo || null,
        notes: notes.trim() || null,
        closeCurrent: !isEdit && closeCurrent,
      })
      onClose()
    } catch (e) {
      setError(String(e))
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={isEdit ? 'Kennung bearbeiten' : 'Kennung hinzufügen'}
      subtitle={
        <span>
          {schoolName}
          {lieferant && (
            <>
              {' · '}
              <span className="font-medium text-on-surface">{lieferant.shortLabel}</span>
              <span className="text-on-surface-variant"> ({lieferant.identifierLabel})</span>
            </>
          )}
        </span>
      }
      footer={
        <>
          {isEdit && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={upsert.isPending || remove.isPending}
              loading={remove.isPending}
            >
              {deleteStage === 1 ? 'Wirklich löschen?' : 'Löschen'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={upsert.isPending || remove.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canSave || upsert.isPending}
            loading={upsert.isPending}
          >
            {isEdit ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <section className="rounded-2xl bg-surface-container-lowest border border-outline-variant/20 p-5 space-y-4">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            {lieferant?.identifierLabel ?? 'Kennung'}
          </h3>
          <TextInput
            label={lieferant?.identifierLabel ?? 'Kundennummer'}
            required
            value={customerNumber}
            onChange={(e) => setCustomerNumber(e.target.value)}
            placeholder={lieferant?.identifierFormatHint || 'z.B. 100247'}
          />
          {lieferant?.identifierFormatHint && (
            <p className="text-[11px] text-on-surface-variant -mt-2">
              Format: {lieferant.identifierFormatHint}
            </p>
          )}
          {istRosenkrantz && (
            <p className={`text-[11px] -mt-2 ${istBlankeRosenkrantzKennung ? 'text-error' : 'text-on-surface-variant'}`}>
              Kundennummer-Adress-id, z. B. 51012-148 — die Adress-id steht im Konto-Test der Rosenkrantz-Konten
            </p>
          )}
        </section>

        <section className="rounded-2xl bg-surface-container-lowest border border-outline-variant/20 p-5 space-y-4">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            Gültigkeit (SCD Type 2)
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              label="Gültig ab"
              required
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
            />
            <TextInput
              label="Gültig bis (optional)"
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              placeholder="leer = weiterhin gültig"
            />
          </div>
          {!isEdit && hasExistingCurrent && (
            <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-3">
              <Toggle
                checked={closeCurrent}
                onChange={(v) => { setToggleBeruehrt(true); setCloseCurrent(v) }}
                label="Vorgänger schließen"
                hint={
                  istZweitesKonto
                    ? `Hier sieht es nach einem ZWEITEN Konto aus (bisher: ${currentCodes.join(', ')}). Zwei Konten können dieselbe Schule beliefern — dann gelten beide Kennungen nebeneinander und keine löst die andere ab. Nur einschalten, wenn die bisherige Kennung wirklich wegfällt.`
                    : 'Setzt das Gültig-bis-Datum der aktuell gültigen Kennung auf einen Tag vor dem neuen Gültig-ab. Standard-SCD2-Verhalten — empfohlen.'
                }
              />
            </div>
          )}
        </section>

        <section className="rounded-2xl bg-surface-container-lowest border border-outline-variant/20 p-5 space-y-4">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            Notizen
          </h3>
          <TextArea
            label="Notizen (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="z.B. Grund der Änderung, Vertragsreferenz, ..."
          />
        </section>

        {error && (
          <p className="text-sm text-rose-600 px-1">{error}</p>
        )}
      </div>
    </Modal>
  )
}
